import type {
  ProcessedBetEntry,
  ProcessedEntry,
} from "@/hooks/firestore/betting/useEntries";
import {
  isSpiceLevel,
  type MysteryMulti,
} from "@/features/sem-generator/util/constants";

import BigNumber from "bignumber.js";
import {
  isBetEntry,
  isQuaddieSelection,
  isSingleSelection,
} from "./type-guards";
import { getSelectionStatus } from "./selection-helpers";

export function isEntryCashedOut(entry: ProcessedBetEntry): boolean {
  return entry.data.status === "SETTLED" && entry.data.result === "CASHED_OUT";
}

export function isEntryDecided(entry: ProcessedBetEntry): boolean {
  return ["SETTLED", "REJECTED", "CANCELLED", "VOID"].includes(
    entry.data.status,
  );
}

export function isCashOutSuspended(
  entry: ProcessedBetEntry,
  amount?: number,
): boolean {
  const isMultiSelection = entry.data.selections.length > 1;

  return amount === 0 && isMultiSelection && !isEntryDecided(entry);
}

export function isEntrySP(entry: ProcessedBetEntry): boolean {
  return entry.data.selections.some(
    (selection) => selection.odds === 1 && selection.event.eventType === "RACE",
  );
}

export function getOdds(entry: ProcessedBetEntry): number {
  const odds = entry.data.selections
    .reduce((acc, selection) => {
      if (selection.void) return acc;

      // Use settlement odds where possible, except for quaddies
      const actualOdds = !isQuaddieSelection(selection)
        ? selection.settlementOdds
        : selection.odds;

      return acc.multipliedBy(actualOdds);
    }, new BigNumber(1))
    .dp(2, BigNumber.ROUND_FLOOR);

  return odds.toNumber();
}

export function getEntryStatus(
  entry: ProcessedBetEntry,
): [string, "neutral" | "success" | "danger" | "warning"] {
  const { status, result } = entry.data;

  if (status === "CANCELLED") {
    return ["CANCELLED", "danger"];
  }

  if (status === "REJECTED") {
    return ["REJECTED", "neutral"];
  }

  if (status === "SETTLED") {
    if (result === "WIN") return ["WON", "success"];
    if (result === "LOSE") return ["LOST", "neutral"];
    if (result === "CASHED_OUT") return ["CASHED OUT", "neutral"];
  }

  if (result === "VOID" || status === "VOID") {
    return ["VOID", "neutral"];
  }

  return ["UNKNOWN", "neutral"];
}

const statusMap = {
  WON: "success",
  PLACED: "success",
  CANCELLED: "neutral",
  LOST: "neutral",
  LIVE: "warning",
} as const;

export const getPickemEntryStatus = (status: keyof typeof statusMap) => {
  if (status === "LIVE") return {};

  return statusMap[status]
    ? {
        variant: statusMap[status],
        text: status,
      }
    : {};
};

export const calculateTotalLegsIncludingSubOutcomes = (
  entry: ProcessedBetEntry,
) => {
  let totalLegs = 0;

  entry.data.selections.forEach((selection) => {
    const outcomes = !isSingleSelection(selection)
      ? Object.values(selection.outcomes)
      : [];

    totalLegs += outcomes.length || 1;
  });

  return totalLegs;
};

export const sortEntries = (
  entries: ProcessedEntry[],
  tab: string | string[],
): ProcessedEntry[] => {
  if (typeof tab !== "string") {
    // we are including more than one tab, skip sorting

    return entries;
  }

  const findClosestStartTime = (entry: ProcessedEntry): Date | null => {
    return entry.data.selections
      .filter((selection) =>
        isBetEntry(entry)
          ? getSelectionStatus(selection) === null
          : selection.result === "UNDECIDED",
      ) // Filter our picks that have a status
      .reduce((closestStartTime: Date | null, selection): Date | null => {
        if (!selection.event.scheduledStartTime) return closestStartTime;
        const selectionStartTime = new Date(selection.event.scheduledStartTime);
        return !closestStartTime || selectionStartTime < closestStartTime
          ? selectionStartTime
          : closestStartTime;
      }, null);
  };

  let sorted = [...entries].sort((a, b) => {
    // find the earliest start time out of all selections for both a and b
    const aStartTime = findClosestStartTime(a);
    const bStartTime = findClosestStartTime(b);

    if (!aStartTime || !bStartTime) {
      return 0;
    }

    return aStartTime > bStartTime ? 1 : -1;
  });

  if (tab === "live") {
    // live entries should be sorted by start time and also entries with statusText "LIVE" should be first
    sorted = sorted.sort((a, b) => {
      const aPick = a.data.selections.find(
        (selection) => getSelectionStatus(selection)?.message === "LIVE",
      );
      const bPick = b.data.selections.find(
        (selection) => getSelectionStatus(selection)?.message === "LIVE",
      );

      if (aPick && bPick) {
        if (
          !aPick.event.scheduledStartTime ||
          !bPick.event.scheduledStartTime
        ) {
          return 0;
        }
        return aPick.event.scheduledStartTime < bPick.event.scheduledStartTime
          ? -1
          : 1;
      }
      if (aPick) {
        return -1;
      }
      if (bPick) {
        return 1;
      }

      return 0;
    });
  }

  return sorted;
};

export const shouldShowPromoIcon = (
  entry: ProcessedBetEntry,
  isPreview?: boolean,
): boolean => {
  const isShared = !!entry.shareId;
  return (
    !(isShared || isPreview) &&
    (entry.data.automationProgressions?.length ?? 0) > 0
  );
};

export const mysteryMultiOf = (
  multiBuilderType: ProcessedBetEntry["data"]["multiBuilderType"],
  mysteryMultiLevel: ProcessedBetEntry["data"]["mysteryMultiLevel"],
): MysteryMulti | undefined => {
  if (multiBuilderType !== "MYSTERY") return undefined;
  return { level: isSpiceLevel(mysteryMultiLevel) ? mysteryMultiLevel : null };
};
